import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import ClientPrivateThread from './ClientPrivateThread';
import type { ClientMsgRow } from './ClientPrivateThread';

interface MerchantInfo {
  id: number;
  name: string;
  image: string | null;
}

interface Thread {
  merchantId: number;
  merchant: MerchantInfo | null;
  lastMessage: string;
  lastAt: string;
  unread: number;
}

function shortTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

/**
 * Onglet « Messages » de l'espace client — DÉDIÉ AUX MESSAGES PRIVÉS.
 * Le Chat IA vit désormais sur sa propre page (onglet « Chat IA »), pour ne
 * plus mélanger l'assistant et les conversations avec les boutiques.
 */
export default function ClientPrivateMessagesTab() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const fetchThreads = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('messages')
        .select('id, sender_id, receiver_id, message, created_at, is_read, attachment_url, attachment_name, attachment_type')
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .order('created_at', { ascending: true })
        .limit(2000);
      if (err) throw err;

      const rows = (data || []) as ClientMsgRow[];
      const map = new Map<number, ClientMsgRow[]>();
      for (const m of rows) {
        const other = m.sender_id === user.id ? m.receiver_id : m.sender_id;
        if (!other || other === user.id) continue;
        const bucket = map.get(other);
        if (bucket) bucket.push(m);
        else map.set(other, [m]);
      }

      const otherIds = Array.from(map.keys());
      const merchants: Record<number, MerchantInfo> = {};
      if (otherIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, name, nomcommerce, user_name, image')
          .in('id', otherIds);
        (users || []).forEach((u: { id: number; name?: string | null; nomcommerce?: string | null; user_name?: string | null; image?: string | null }) => {
          merchants[u.id] = {
            id: u.id,
            name: u.nomcommerce || u.name || u.user_name || `Boutique #${u.id}`,
            image: u.image || null,
          };
        });
      }

      const list: Thread[] = [];
      map.forEach((msgs, merchantId) => {
        const last = msgs[msgs.length - 1];
        const unread = msgs.filter((m) => m.receiver_id === user.id && !m.is_read).length;
        list.push({
          merchantId,
          merchant: merchants[merchantId] || null,
          lastMessage: last.message || (last.attachment_name ? `Pièce jointe : ${last.attachment_name}` : ''),
          lastAt: last.created_at,
          unread,
        });
      });
      list.sort((a, b) => new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime());
      setThreads(list);
    } catch {
      setError('Impossible de charger vos conversations.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => (t.merchant?.name || '').toLowerCase().includes(q) || t.lastMessage.toLowerCase().includes(q));
  }, [threads, search]);

  const selected = useMemo(() => threads.find((t) => t.merchantId === selectedId) || null, [threads, selectedId]);
  const totalUnread = threads.reduce((sum, t) => sum + t.unread, 0);

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-5">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-chat-smile-3-line text-accent-500"></i>
          Messages
          {totalUnread > 0 && (
            <span className="min-w-[20px] h-[20px] px-1.5 rounded-full bg-accent-500 text-background-50 text-[11px] font-bold flex items-center justify-center">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </h2>
        <p className="text-sm text-foreground-500 mt-1">
          Vos échanges privés avec les boutiques. Pour discuter avec l&apos;assistant, ouvrez « Chat IA ».
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Liste */}
        <div className={`${selected ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-80 flex-shrink-0`}>
          <div className="relative mb-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une boutique..."
              className="w-full pl-9 pr-3 py-2.5 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300"
            />
          </div>
          <div className="flex-1 overflow-y-auto space-y-1.5 max-h-[60vh] lg:max-h-[calc(100vh-320px)]">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                <p className="text-sm text-red-500 mb-3">{error}</p>
                <button onClick={fetchThreads} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-xs font-medium cursor-pointer hover:bg-primary-600 whitespace-nowrap">Réessayer</button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center text-foreground-400 px-4">
                <i className="ri-chat-3-line text-3xl mb-2"></i>
                <p className="text-sm">Aucune conversation</p>
                <p className="text-xs mt-1">Vos échanges avec les boutiques apparaîtront ici.</p>
              </div>
            ) : (
              filtered.map((t) => {
                const name = t.merchant?.name || `Boutique #${t.merchantId}`;
                const active = t.merchantId === selectedId;
                return (
                  <button
                    key={t.merchantId}
                    type="button"
                    onClick={() => setSelectedId(t.merchantId)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors cursor-pointer border ${
                      active ? 'bg-accent-100/70 border-accent-200' : 'bg-background-50 border-background-200/70 hover:bg-background-100/70'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {t.merchant?.image ? (
                        <img src={t.merchant.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-bold text-accent-700">{name.charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-foreground-900 truncate">{name}</span>
                        <span className="text-[10px] text-foreground-400 whitespace-nowrap flex-shrink-0">{shortTime(t.lastAt)}</span>
                      </div>
                      <p className="text-xs text-foreground-500 truncate mt-0.5">{t.lastMessage}</p>
                    </div>
                    {t.unread > 0 && (
                      <span className="w-5 h-5 rounded-full bg-primary-500 text-background-50 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {t.unread}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Fil */}
        <div className={`${selected ? 'flex' : 'hidden lg:flex'} flex-col flex-1 min-w-0`}>
          {selected ? (
            <ClientPrivateThread
              userId={user.id}
              merchantId={selected.merchantId}
              merchantName={selected.merchant?.name || `Boutique #${selected.merchantId}`}
              merchantImage={selected.merchant?.image}
              onBack={() => setSelectedId(null)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 min-h-[420px] bg-background-50 border border-background-200/70 rounded-lg text-center px-6">
              <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                <i className="ri-chat-3-line text-2xl text-accent-600"></i>
              </div>
              <p className="text-sm font-medium text-foreground-800">Sélectionnez une conversation</p>
              <p className="text-xs text-foreground-500 mt-1">Choisissez une boutique à gauche pour lire et répondre.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}